import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type WorkItem } from '@/features/jobs/bookkeeping/BookkeepingProgressionInfo'
import { type BookkeepingProgressionStore } from '@/features/jobs/bookkeeping/BookkeepingProgressionStore'
import { action, computed } from 'mobx'
import { task } from 'mobx-task'
import type { Id } from '@/infra/id'
import { ViewModelList } from '@/infra/view-model-list'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { CompletedWorkItemViewModel } from '@/features/jobs/details/components/BookkeepingCompleteCycleDialog/CompletedWorkItemViewModel'

/**
 * Completes the work items of the bookkeeping cycle in progress.
 */
export class BookkeepingCompleteCycleDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  private readonly _workItemVms: ViewModelList<
    WorkItem,
    CompletedWorkItemViewModel
  >

  readonly modal = new ModalViewModel()

  constructor(
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _jobId: Id,
    private readonly _bookkeepingProgressionStore: BookkeepingProgressionStore,
  ) {
    this._workItemVms = new ViewModelList({
      models: () =>
        this.bookkeepingProgressionInfo?.workItems.filter(
          (wi) => wi.remaining > 0,
        ) ?? [],
      create: (wi) => new CompletedWorkItemViewModel(wi),
    })
    this._lifecycle.manage(this._workItemVms)
  }

  /**
   * Returns the bookkeeping progression info
   */
  @computed
  get bookkeepingProgressionInfo() {
    return this._bookkeepingProgressionStore.bookkeepingProgressions.get(
      this._jobId,
    )
  }

  /**
   * View models for service items to complete in the current cycle
   */
  @computed
  get itemsToComplete() {
    return this._workItemVms.items.slice()
  }

  /**
   * The work items with a completed count.
   */
  @computed
  get completedItems() {
    return this.itemsToComplete.filter((itc) => itc.completedItems > 0)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Validates each work item
   */
  @action.bound
  validate() {
    return this.itemsToComplete.every(
      (ci) => ci.validation.reset().validate().isValid,
    )
  }

  /**
   * Resets each work item
   */
  @action.bound
  reset() {
    this.itemsToComplete.forEach((ci) => ci.reset())
  }

  /**
   * Completes the current cycle
   */
  submit = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    if (!this.completedItems.length) {
      this._newAlert()
        .withState('error')
        .saying('There are no items to complete')
        .show()
      return
    }

    if (this.bookkeepingProgressionInfo?.currentCycle) {
      await this._bookkeepingProgressionStore
        .completeBookkeepingCycle({
          jobId: this._jobId,
          cycleId: this.bookkeepingProgressionInfo.currentCycle.id,
          completedItems: this.completedItems.map((ci) => ({
            id: ci.workItemId,
            completedCount: ci.completedItems,
          })),
        })
        .catch(
          this._createAlertErrorHandler(
            'Completing the bookkeeping cycle failed',
          ),
        )
    } else {
      this._newAlert()
        .withState('error')
        .saying('There is no cycle currently in progress')
        .show()
    }

    this.reset()
    this.modal.close()
  })
}
